import styled from 'styled-components';
import { useMemo } from 'react';
import type { Client } from '@/models/client.server';
import { ButtonLink } from './Button';
import EyeIcon from '@/icons/Eye';
import { Text } from './Text';
import ClientIcon from '@/icons/Client';
import AddeparIcon from '@/icons/Addepar';
import type theme from '@shared/theme';
import type { ClientStatus } from '@shared/entities/ClientStatus';
import { GhostBlock } from '@/components/GhostComponents/GhostBlock';

type Props = {
    client: Client;
    className?: string;
    isPrivateMode?: boolean; // when true obfuscates client name
};

function getStatusIndicatorColor(
    status: ClientStatus
): keyof typeof theme.colors {
    switch (status) {
        case 'READY':
            return 'green';
        case 'LOADING_PORTFOLIO':
            return 'yellow';
        case 'FAILED_LOADING_PORTFOLIO':
            return 'red';
        default:
            return 'lightGrey';
    }
}

const UnstyledClientListItem = ({
    client,
    className,
    isPrivateMode = false,
}: Props) => {
    const latestCompletedProfiler = client.latestCompletedProfiler;
    const noOfPortfolios = client.portfolios.length;

    const isAddeparClient = client.source === 'ADDEPAR';

    const isProfilerCompleted =
        latestCompletedProfiler &&
        latestCompletedProfiler.status === 'completed';

    // Stable random width for ghost name between 120px and 180px
    const ghostNameWidth = useMemo(
        () => `${Math.round(120 + Math.random() * 60)}px`,
        []
    );

    return (
        <div
            className={className}
            data-testid={`client-${client.clientReference}`}
        >
            <ClientName>
                <IconWrapper>
                    <ClientIcon strokeColor="uNavy" fillColor="uNavy10" />
                    {isAddeparClient && (
                        <SourceIconToken>
                            <AddeparIcon />
                        </SourceIconToken>
                    )}
                </IconWrapper>

                {isPrivateMode ? (
                    <GhostBlock
                        style={{ width: ghostNameWidth, height: '10px' }}
                        $animated={false}
                    />
                ) : (
                    <Text weight="bold">{client.name}</Text>
                )}
            </ClientName>

            <ClientInfo>
                <StatusIndicator
                    $indicatorColor={isProfilerCompleted ? 'green' : 'red'}
                >
                    Profiler {isProfilerCompleted ? 'complete' : 'incomplete'}
                </StatusIndicator>

                {isAddeparClient && (
                    <StatusIndicator
                        $indicatorColor={getStatusIndicatorColor(client.status)}
                    >
                        {client.status === 'READY' &&
                            noOfPortfolios === 0 &&
                            'No portfolios'}
                        {client.status === 'READY' &&
                            noOfPortfolios === 1 &&
                            `1 portfolio`}
                        {client.status === 'READY' &&
                            noOfPortfolios > 1 &&
                            `${noOfPortfolios} portfolios`}
                        {client.status === 'LOADING_PORTFOLIO' &&
                            'Sync in progress'}
                        {client.status === 'FAILED_LOADING_PORTFOLIO' &&
                            'Failed to sync'}
                    </StatusIndicator>
                )}

                {client.source === 'ETCHO' && (
                    <StatusIndicator
                        $indicatorColor={noOfPortfolios > 0 ? 'green' : 'red'}
                    >
                        {noOfPortfolios > 0
                            ? `${noOfPortfolios} portfolio${
                                  noOfPortfolios > 1 ? 's' : ''
                              }`
                            : 'No portfolios'}
                    </StatusIndicator>
                )}

                <ButtonLink
                    $variant="lineIcon"
                    to={`/client/${client.clientId}`}
                    prefetch="intent"
                    data-testid="view-client"
                >
                    <Text as="span" size="small">
                        View client
                    </Text>{' '}
                    <EyeIcon width={20} height={20} />
                </ButtonLink>
            </ClientInfo>
        </div>
    );
};

export const ClientListItem = styled(UnstyledClientListItem)`
    display: flex;
    padding: 12px;
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: 8px;
    flex-direction: row;
    gap: 20px;
`;

const IconWrapper = styled.div`
    position: relative;
`;

const SourceIconToken = styled.div`
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 2;

    height: 16px;
    width: 16px;
    border-radius: 50%;
    border: solid 1px ${({ theme }) => theme.colors.lightNavy};
    background: ${({ theme }) => theme.colors.white};
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2px;
`;

const ClientName = styled.div`
    display: flex;
    align-items: center;

    width: 100%;
    gap: 20px;
    flex: 2;
`;

const ClientInfo = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 3;
`;

const StatusIndicator = styled(Text)<{
    $indicatorColor: keyof typeof theme.colors;
}>`
    padding-left: 26px;
    position: relative;
    min-width: 125px;

    &:before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background-color: ${({ theme, $indicatorColor }) =>
            theme.colors[$indicatorColor]};
    }
`;
